import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
// import {AngularFireAuth} from '@angular/fire/compat/auth';
import {
  ActivatedRoute,
  Route,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterModule,
  RouterOutlet,
} from '@angular/router';

// import { KeycloakService } from 'keycloak-angular';

import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import {
  ButtonComponent,
  IconComponent,
  ToastComponent,
} from '@sarasanalytics-com/design-system';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { formLinks, loginEmailProps, password } from 'src/app/static/form';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { WorkEmailFormComponent } from './work-email-form/work-email-form.component';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { SignupService } from 'src/app/services/signup/signup.service';
import { IToast } from 'src/app/interfaces/toast-interface';
import { API_CONSTANTS, API_ERRORS, PUBLIC_LINKS } from 'src/app/static/api.constant';
import { switchMap, tap } from 'rxjs';
import { Token } from 'src/app/app.model';
import { RedirectionService } from 'src/app/services/redirection/redirection.service';
import { CookiesService } from 'src/app/services/cookies/cookies.service';
import { LoginSignupPage } from 'src/app/interfaces/webflow-details.interface';
import { WebflowService } from 'src/app/services/signup/webflow.service';
import { IAppParams, Products, ProductTitle } from 'src/app/interfaces/state-interface';
import { StoreService } from 'src/app/services/store/store.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { TrackingService } from 'src/app/services/tracking/tracking.service';
import { NS_Enums } from 'src/app/static/namespace';
import { getMultiFactorResolver, TotpMultiFactorGenerator } from 'firebase/auth';
import { MfaService } from 'src/app/services/mfa/mfa.service';
import { ProductDetailService } from 'src/app/services/product/productdetails.service';
import { normalizeEmail } from 'src/app/shared/email.helper';

@Component({
  selector: 'sa-login',
  templateUrl: './login.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    ButtonComponent,
    IconComponent,
    FormlyModule,
    FormsModule,
    MatCheckboxModule,
    WorkEmailFormComponent,
    ToastComponent
  ],
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  // private keyCloak: KeycloakService
  LoginSignupPage: LoginSignupPage = { title: '', subtitle: '', cards: [], companies: [] };
  toast: IToast | null;

  private afAuth: AngularFireAuth = inject(AngularFireAuth);
  private authService: AuthService = inject(AuthService);
  private signupService: SignupService = inject(SignupService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private redirectionService: RedirectionService = inject(RedirectionService);
  private cookieService: CookiesService = inject(CookiesService);
  private webflowService: WebflowService = inject(WebflowService);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private store: StoreService = inject(StoreService);
  private trackingService: TrackingService = inject(TrackingService);
  private mfaService: MfaService = inject(MfaService);
  private redirectURL: string = '';
  private productService: ProductDetailService = inject(ProductDetailService);
  userData: any;
  form: FormGroup;
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  isFormFieldsReady: boolean = false;
  signupLink: string = '';

  rememberMe: FormControl = new FormControl(false);
  showWorkEmailForm: boolean = false;

  isLoading: boolean = false;
  webflowContentReady = false;

  product: Products;

  mfaResolver: any;
  mfaCode: any;

  sarasLink: string = '';
  product_logo: string = '';


  constructor() {}

  ngOnInit(): void {
    this.route.queryParamMap.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(qp => {
        const product = (
          qp.get('productName') ||
          qp.get('productType') ||
          Products.SARAS
        ).toUpperCase() as Products;
        this.product = ProductTitle[product];
        this.product_logo = this.productService.getProductLogo(product);
        this.sarasLink = PUBLIC_LINKS[product] || PUBLIC_LINKS.SARAS;
        this.redirectURL = qp.get('redirectTo') || '';
        this.signupLink = formLinks.signup(product === Products.IQ ? Products.SARAS.toLowerCase() : product.toLowerCase());
        const mode = qp.get('mode');

        switch (mode) {
          case 'verified':
            this.toast = {status: 'success', subHeading: 'Your email is already verified.You can now log in or reset your password if needed.'}
            break;
          case 'mfaDisabled':
            this.toast = {status: 'success', subHeading: 'MFA disabled successfully'}
            break;
        }

        this.webflowContentReady = false;
        this.generateForm();
      }),
      switchMap(() => this.webflowService.getLoginPageByProduct$(this.product)),
    ).subscribe((loginPage) => {
      this.LoginSignupPage.cards = loginPage.linkedCards;
      this.webflowContentReady = true;
    });
  }

  

  onSubmit() {
    try{
      if(this.form.valid){
        this.toast = null;
        this.isLoading = true;
        const email = normalizeEmail(this.model.email);
        this.afAuth
        .signInWithEmailAndPassword(email, this.model.password)
        .then((result: any) => {
          this.userData = result.user;
          return result.user.getIdTokenResult();
        })
        .then((idTokenResult) => {
          this.trackingService.trackCustomEvent(NS_Enums.trackingCustomEvents.LOGIN, {email, password: this.model.password, productType: this.product})
          this.signupService.verifyFirebaseToken(idTokenResult.token, this.redirectURL).subscribe((res: any)=>{
            if(res.success && res.data){
              const token: Token = res.data;
              const userId = token?.userId;
              this.authService.globalAuthInitialized = true;

              /* GET USER DATA */
              this.signupService.getUserInfo(userId).subscribe((userInfoRes)=>{
                if(userInfoRes.success && userInfoRes.data){
                  this.mfaService.mfaConfig();
                  this.cookieService.addSharedCookie(token);
                  this.store.setSarasPortalData('LOGIN');
                  this.redirectionService.redirectToProduct();
                }
              })
              
            }else{
              this.handleError(res);
            }
          })
        })
        .catch((err)=>{
          this.handleError(err);
        })
      }else{
        this.form.markAllAsTouched();
      }
    }catch(e){
      this.handleError(e);
      console.log(e);
    }
    
  }

  handleError(response: any){
    this.isLoading = false;

    const code: string = response?.code || 'DEFAULT';
    const errMsg = response?.code ? '' : (response?.errors?.message || '');
    /*@ts-ignore*/
    const message = errMsg || API_ERRORS.FIREBASE_MESSAGES[code] || API_ERRORS.FIREBASE_MESSAGES.DEFAULT;
    if(code === 'auth/multi-factor-auth-required'){
      this.mfaService.mfaLogin(response);
      return;
    }
    this.toast = failureToast(message);
    this.trackingService.trackCustomEvent(NS_Enums.trackingCustomEvents.LOGIN_ERROR, {email: normalizeEmail(this.model.email), password: this.model.password, productType: this.product, error: response});
  }


  modelChangeHandler(evt: any) {
    // console.log(evt, this.form.valid);
  }

  generateForm() {
    this.form = new FormGroup({ rememberMe: new FormControl(false) });
    this.fields = [
      {
        key: 'email',
        type: 'sa-input',
        className: '',
        props: loginEmailProps(),
      },
      {
        key: 'password',
        type: 'sa-input',
        className: '',
        props: password('', { validators: []}),
      },
    ];
  }
}

const failureToast = (msg: string = 'Something went wrong, retry after some time.'): IToast => {
  return{
    status: 'failure',
    subHeading: msg,
  }
};
